// ── Task detail slide-out panel ──

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, renderMarkdown, formatDate, renderAvatarHtml, TYPES, TYPE_COLORS, TYPE_LABELS, getTypeIcon, sprintOptionsHtml, nextSprintNumber, canStartTask, availableStatuses } from '../utils.js';
import { removeAssigneePicker } from './assignee-picker.js';
import { renderMemberCombo, createMemberCombo } from './member-combo.js';
import { reserveTaskKey, releaseTaskKey } from '../lib/task-key-reservation.js';
import { attachImageHandlers } from '../lib/image-paste.js';
import { attachEmbedControls, closeAnyOpenEmbedMenu } from '../lib/embed-menu.js';
import { navigate } from '../router.js';
import { loadStatuses, statusNames, statusLabel, statusBadgeAttrs, roleStatus } from '../lib/statuses.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { groupLabelSingular } from '../lib/task-group-label.js';
import { loadProject } from '../lib/project.js';
import { loadDevices } from '../lib/devices.js';
import { buildStartTaskUrl } from '../lib/task-app-link.js';
import { rawOffsetFromRendered } from '../lib/caret-map.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { getSeenEventId, setSeenEventId, unreadEventIds, maxEventId } from '../lib/task-events-seen.js';
import { highlightMentions } from '../lib/mention-highlight.js';
import { attachMentionTypeahead, closeAnyOpenMentionTypeahead } from '../lib/mention-typeahead.js';

let panelEl = null;
let overlayEl = null;
let escHandler = null;
let currentState = null;
let comments = [];
let assigneeCombo = null;
let tagTypeahead = null;
// TPT33 — @mention typeahead on the compose box (recreated every paint, same
// destroy-and-recreate discipline as assigneeCombo/tagTypeahead above) plus the
// single in-place inline comment editor (at most one open at a time — module globals
// for the same reason `comments` is: refreshPanelContent() wipes currentState.root
// wholesale, so anything mid-edit has to live outside that DOM to survive/be reset).
let commentMentionTypeahead = null;
let editingCommentId = null;
let editCommentMentionTypeahead = null;
// Bumped on every openTaskDetail() and snapshotted by closeTaskDetail()'s deferred onClose
// timer (C1261) — if a new panel opened before that timer fires (close card A, open card B
// within the 220ms window), the sequence no longer matches and the stale onClose is dropped
// instead of firing a page-owner reload that would force-close the freshly opened panel B.
let panelSeq = 0;

// ── Notifications tab (TPT18) ── module globals, same lifecycle discipline as `comments`
// above: renderPanelContent()/refreshPanelContent() wipe currentState.root.innerHTML
// wholesale, so anything that must survive a repaint (which tab is active, which rows
// have already scrolled into view this session) has to live outside the DOM.
let taskEvents = [];             // newest-first, as returned by GET .../events
let eventsSubscribed = true;     // this user's effective `subscribed` state
let activeTab = 'details';       // 'details' | 'comments' | 'notifications'
let observedEventIds = new Set(); // event ids the IntersectionObserver has already seen this session
let eventObserver = null;

function ensurePanel() {
  if (panelEl && overlayEl) return;

  overlayEl = document.createElement('div');
  overlayEl.className = 'task-detail-overlay';
  overlayEl.addEventListener('click', () => closeTaskDetail());

  panelEl = document.createElement('div');
  panelEl.className = 'task-detail-panel';

  document.body.appendChild(overlayEl);
  document.body.appendChild(panelEl);
}

// (TPT18) task_events.event_type -> display label. Falls back to the raw string in
// renderPanelContent() so a future server-side event type still renders instead of
// disappearing — this map is a display nicety, not a whitelist.
const EVENT_TYPE_LABELS = {
  task_created: 'Created',
  status_changed: 'Status',
  assigned: 'Assigned',
  comment: 'Comment',
};

function getSprintOptions(currentPriority, sprints, allTasks, label) {
  const next = nextSprintNumber(sprints, allTasks);
  return sprintOptionsHtml(sprints, allTasks, currentPriority, label)
    + `<option value="${next}">+ New ${escapeHtml(label)}</option>`;
}

// descriptionHtml(text, members) / commentBodyHtml(content, members) (TPT33) — the
// single rendering path for "raw markdown -> highlighted HTML" used by every
// description/comment render site in this file (renderPanelContent()'s initial paint,
// startDescriptionEdit()'s save(), and renderCommentRow() below). highlightMentions()
// is NOT idempotent, so it must only ever wrap a FRESH renderMarkdown() call — never
// re-applied to already-highlighted output — which is why every call site goes
// through these two helpers instead of calling renderMarkdown()+highlightMentions()
// inline.
function descriptionHtml(text, members) {
  return text
    ? highlightMentions(renderMarkdown(text), members)
    : '<span class="text-muted">No description</span>';
}

function commentBodyHtml(content, members) {
  return highlightMentions(
    renderMarkdown((content || '').replace(/<!--\s*trello-action:[^\s>]*\s*-->/g, '')),
    members
  );
}

// renderCommentRow(c, members) (TPT33) — the single comment-row renderer, shared by
// the full paint (renderPanelContent(), comments.map() above) and the post-edit
// in-place DOM swap (saveCommentEdit()/cancelCommentEdit() below) so the two paths
// can never drift. `canEdit` mirrors the PATCH route's own author-only 403 check
// (routes/tasks.js) — the Edit button only ever appears where the server would also
// accept the edit. `updated_at` (TPT31, NULL = never edited) drives the "(edited)"
// marker.
function renderCommentRow(c, members) {
  const me = getUser();
  const canEdit = !!(me && c.user && c.user.id === me.id);
  return `
                <div class="task-detail-comment" data-comment-id="${Number(c.id)}" data-comment-type="${escapeHtml(c.comment_type || 'comment')}">
                  <div class="task-detail-comment-meta">
                    ${c.comment_type === 'resolution' ? '<span class="badge-resolution">Resolution</span>' : ''}
                    <strong>${escapeHtml(c.user?.name || c.user?.email || 'Unknown')}</strong>
                    <span class="text-muted text-sm">${formatDate(c.created_at)}</span>
                    ${c.updated_at ? `<span class="task-detail-comment-edited" title="Edited ${escapeHtml(formatDate(c.updated_at))}">(edited)</span>` : ''}
                    ${canEdit ? '<button type="button" class="btn btn-ghost btn-sm task-detail-comment-edit-btn">Edit</button>' : ''}
                  </div>
                  <div class="task-detail-comment-body">${commentBodyHtml(c.content, members)}</div>
                </div>`;
}

function renderPanelContent(task, members, children) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  const deps = task.dependencies || [];
  const isPage = currentState.mode === 'page';
  const showSprint = sprintsEnabled(currentState.project); // C1334
  const sprintLabelSingular = groupLabelSingular(currentState.project && currentState.project.task_group_label); // C1395
  const detailHref = (k) => `#/projects/${currentState.projectId}/tasks/${encodeURIComponent(k)}`;

  // (C1389, widened C1559) "Start in Task App" — every task now gets this button; which
  // of the 3 branches fires (seed an origin-linked planning chat / start the first
  // startable child / start the task directly) is decided server-side by the Task App's
  // /start-task route, never here — the web app can't see the real child set reliably
  // (children below is options.allTasks-derived and degrades to [] on a failed prefetch,
  // plus goes stale between render and click). Gated on device linkage (lib/devices.js,
  // currentState.devices — fetched alongside statuses/project by both openTaskDetail()
  // and mountTaskDetail(), same discipline as § Sprint Dropdown / § Dynamic statuses
  // above). No localhost reachability probe — Safari mixed-content and Chrome Private
  // Network Access make that unreliable; the real reachability/account/project check
  // happens server-side once clicked (see tt-objective-chat.md § Web→Task App handoff).
  const startInTaskAppHtml = (() => {
    const user = getUser();
    const linkedProjectIds = new Set(
      (currentState.devices || []).flatMap(d => d.project_ids || []).map(String)
    );
    const linked = user && linkedProjectIds.has(String(currentState.projectId));
    if (!linked) {
      return `<button class="btn btn-ghost btn-sm task-detail-start-in-app" disabled title="${escapeHtml('No linked Task App device found for this project — run Task App setup on that machine.')}">Start in Task App</button>`;
    }
    const url = buildStartTaskUrl({ projectId: currentState.projectId, taskKey: task.task_key, userId: user.id });
    return `<a class="btn btn-ghost btn-sm task-detail-start-in-app" href="${escapeHtml(url)}" target="_blank" rel="noopener">Start in Task App</a>`;
  })();

  // Find parent task from allTasks
  let parentTask = null;
  if (task.parent_id && currentState.options.allTasks) {
    parentTask = currentState.options.allTasks.find(t => t.id === task.parent_id);
  }

  // ── Notifications tab badge (TPT18) ── unread = event id past this user's last-seen
  // marker AND not authored by this user (see lib/task-events-seen.js). Rows already
  // scrolled into view this session (observedEventIds, live-decremented by
  // observeUnreadEvents()) are excluded so the badge count matches what's still actually
  // unseen on screen, not just what's unseen-per-the-persisted-marker.
  const currentUser = getUser();
  const seenEventId = getSeenEventId(currentState.projectId, task.task_key);
  const unreadCount = unreadEventIds(taskEvents, seenEventId, currentUser && currentUser.id)
    .filter(id => !observedEventIds.has(id)).length;
  const notifBadgeHtml = unreadCount
    ? `<span class="task-detail-tab-badge">${unreadCount > 99 ? '99+' : unreadCount}</span>`
    : '';

  let html = `
    <div class="task-detail-header">
      <div class="task-detail-key-row">
        <span class="badge ${catClass}">${escapeHtml(task.task_key)}</span>
        ${getTypeIcon(task.type || 'task')}
        <span class="task-detail-type-label" style="color:${TYPE_COLORS[task.type] || TYPE_COLORS.task};font-size:0.8rem;font-weight:600">${TYPE_LABELS[task.type] || 'Task'}</span>
        ${currentState.mode === 'panel' ? '<button class="task-detail-close" title="Close">&times;</button>' : ''}
      </div>
      <div class="task-detail-field">
        <label class="task-detail-field-label">Type</label>
        <select class="form-select form-select-sm task-detail-type-select">
          ${TYPES.map(t => `<option value="${t}" ${t === task.type ? 'selected' : ''}>${TYPE_LABELS[t]}</option>`).join('')}
        </select>
      </div>
    </div>

    <div class="task-detail-body">
      <h2 class="task-detail-title" title="Click to edit">${escapeHtml(task.title)}</h2>
      ${startInTaskAppHtml ? `<div class="task-detail-start-in-app-row">${startInTaskAppHtml}</div>` : ''}

      <div class="task-detail-tabs" role="tablist" aria-label="Task detail views">
        <button class="task-detail-tab${activeTab === 'details' ? ' active' : ''}" type="button" role="tab" data-tab="details" aria-selected="${activeTab === 'details'}">Details</button>
        <button class="task-detail-tab${activeTab === 'comments' ? ' active' : ''}" type="button" role="tab" data-tab="comments" aria-selected="${activeTab === 'comments'}">Comments${comments.length ? ` (${comments.length})` : ''}</button>
        <button class="task-detail-tab${activeTab === 'notifications' ? ' active' : ''}" type="button" role="tab" data-tab="notifications" aria-selected="${activeTab === 'notifications'}">Notifications${notifBadgeHtml}</button>
      </div>

      <div class="task-detail-tab-panel" data-tab="details"${activeTab === 'details' ? '' : ' hidden'}>
        <div class="task-detail-fields">
          <div class="task-detail-field">
            <label class="task-detail-field-label">Status</label>
            <select class="form-select form-select-sm task-detail-status-select">
              ${availableStatuses(statusNames(currentState.statuses), task, getUser(), roleStatus(currentState.statuses, 'in_progress')?.name).map(s => `<option value="${escapeHtml(s)}" ${s === task.status ? 'selected' : ''}>${escapeHtml(statusLabel(s))}</option>`).join('')}
            </select>
          </div>
          ${showSprint ? `
          <div class="task-detail-field">
            <label class="task-detail-field-label">${escapeHtml(sprintLabelSingular)}</label>
            <select class="form-select form-select-sm task-detail-priority-select">
              ${getSprintOptions(task.priority, currentState.options.sprints, currentState.options.allTasks, sprintLabelSingular)}
            </select>
          </div>` : ''}
          <div class="task-detail-field">
            <label class="task-detail-field-label">Member Assignee</label>
            <div class="task-detail-assignee-combo">
              <span class="task-detail-assignee-avatar">${renderAvatarHtml(task.assignee ? members.find(m => m.user_id === task.assignee) || null : null, 24)}</span>
              ${renderMemberCombo({ id: 'task-detail-assignee', assignee: task.assignee, members })}
            </div>
          </div>
          <div class="task-detail-field">
            <label class="task-detail-field-label">Story Points</label>
            <input type="number" class="form-input form-input-sm task-detail-sp-input" min="0" value="${task.story_points != null ? task.story_points : ''}" placeholder="—">
          </div>
          ${(() => { const _bd = task.active_device || task.recent_device; if (!_bd) return ''; const _live = !!task.active_device; return `<div class="task-detail-field"><label class="task-detail-field-label">Device</label><span class="device-badge${_live ? '' : ' device-badge--recent'}" title="${escapeHtml(_live ? `Active on ${_bd.name}` : `Last worked on ${_bd.name}`)}">${escapeHtml(_bd.name)}</span></div>`; })()}
        </div>

        <div class="task-detail-field task-detail-section">
          <label class="task-detail-field-label">Description</label>
          <div class="task-detail-description md-preview" title="Click to edit">${descriptionHtml(task.description, members)}</div>
        </div>

        <div class="task-detail-field task-detail-section">
          <label class="task-detail-field-label">Tags</label>
          <input type="text" class="task-detail-tag-input" placeholder="Add tag...">
        </div>

        ${(deps.length && !task.is_objective) ? `
        <div class="task-detail-field task-detail-section">
          <label class="task-detail-field-label">Dependencies</label>
          <div class="task-detail-deps">
            ${deps.map(d => `<span class="badge badge-dep">${escapeHtml(d)}</span>`).join(' ')}
          </div>
        </div>` : ''}

        <div class="task-detail-field task-detail-section">
          <label class="task-detail-field-label">Parent</label>
          <div class="task-detail-parent">
            ${parentTask
              ? (isPage
                  ? `<a class="detail-parent-link" href="${detailHref(parentTask.task_key)}">${getTypeIcon(parentTask.type || 'task')} ${escapeHtml(parentTask.task_key)} — ${escapeHtml(parentTask.title)}</a>`
                  : `<span class="detail-parent-link" data-task-key="${escapeHtml(parentTask.task_key)}">${getTypeIcon(parentTask.type || 'task')} ${escapeHtml(parentTask.task_key)} — ${escapeHtml(parentTask.title)}</span>`)
                + `<button class="btn btn-ghost btn-sm task-detail-remove-parent">Remove</button>`
              : `<span class="text-muted">None</span>`}
            <button class="btn btn-ghost btn-sm task-detail-change-parent">Change Parent</button>
          </div>
        </div>

        <div class="task-detail-field task-detail-section detail-children">
          <label class="task-detail-field-label">Children ${children.length ? `(${children.length})` : ''}</label>
          ${children.length ? `
          <div class="task-detail-children-list">
            ${children.map(c => `
            <${isPage ? 'a' : 'div'} class="task-detail-child"${isPage ? ` href="${detailHref(c.task_key)}"` : ` data-task-key="${escapeHtml(c.task_key)}"`}>
              ${getTypeIcon(c.type || 'task')}
              <span class="badge badge-sm">${escapeHtml(c.task_key)}</span>
              <span class="task-detail-child-title">${escapeHtml(c.title)}</span>
              <span class="badge badge-sm ${statusBadgeAttrs(currentState.statuses.find(s => s.name === c.status)).className}" style="${statusBadgeAttrs(currentState.statuses.find(s => s.name === c.status)).style}">${escapeHtml(statusLabel(c.status))}</span>
              ${c.story_points != null ? `<span class="text-muted text-sm">${c.story_points}sp</span>` : ''}
            </${isPage ? 'a' : 'div'}>`).join('')}
          </div>` : (task.is_objective
            ? '<div class="text-muted">Subtasks come from the Objective chat in the Task App.</div>'
            : '<div class="text-muted">No children</div>')}
          ${task.is_objective ? '' : '<button class="btn btn-ghost btn-sm task-detail-add-child mt-05">+ Add Child</button>'}
        </div>
      </div>

      <div class="task-detail-tab-panel" data-tab="comments"${activeTab === 'comments' ? '' : ' hidden'}>
        <div class="task-detail-field task-detail-comments">
          <div class="task-detail-comments-list">
            ${comments.length
              ? comments.map(c => renderCommentRow(c, members)).join('')
              : '<div class="text-muted">No comments yet</div>'}
          </div>
          <textarea class="form-textarea task-detail-comment-input" rows="3" placeholder="Add a comment..."></textarea>
          <div class="comment-post-row">
            <button class="btn btn-primary btn-sm task-detail-comment-submit">Post</button>
          </div>
        </div>
      </div>

      <div class="task-detail-tab-panel" data-tab="notifications"${activeTab === 'notifications' ? '' : ' hidden'}>
        <div class="task-detail-subscribe">
          <label><input type="checkbox" class="task-detail-subscribe-toggle" ${eventsSubscribed ? 'checked' : ''}> Subscribed</label>
          <button class="btn btn-ghost btn-sm task-detail-events-refresh" type="button">Refresh</button>
        </div>
        <div class="task-detail-events-list">
          ${taskEvents.length
            ? taskEvents.map(e => `
              <div class="task-detail-event" data-event-id="${e.id}">
                <div class="task-detail-event-meta">
                  <span class="task-detail-event-type">${escapeHtml(EVENT_TYPE_LABELS[e.event_type] || e.event_type)}</span>
                  <strong>${escapeHtml((e.actor && (e.actor.name || e.actor.email)) || 'System')}</strong>
                  <span class="text-muted text-sm">${formatDate(e.created_at)}</span>
                </div>
                <div class="task-detail-event-body">
                  ${escapeHtml(e.title || '')}
                  ${e.body ? `<div class="text-muted text-sm">${escapeHtml(e.body)}</div>` : ''}
                </div>
              </div>`).join('')
            : '<div class="text-muted">No activity yet</div>'}
        </div>
      </div>
    </div>

    <div class="task-detail-footer">
      <div class="task-detail-timestamps">
        <span class="text-muted text-sm">Created ${formatDate(task.created_at)}</span>
        <span class="text-muted text-sm">Updated ${formatDate(task.updated_at)}</span>
      </div>
      <button class="btn btn-danger btn-sm task-detail-delete-btn">Delete</button>
    </div>`;

  return html;
}

async function patchField(field, value, explicitFieldEl = null) {
  if (!currentState) return;
  const { projectId, taskKey } = currentState;
  const fieldEl = explicitFieldEl || currentState.root.querySelector(`.task-detail-${field}-select, .task-detail-sp-input, .task-detail-title, .task-detail-description`);

  try {
    const { task } = await api.patch('/projects/' + projectId + '/tasks/' + taskKey, { [field]: value });
    currentState.task = task;

    // Sync back to allTasks
    const allTasks = currentState.options.allTasks;
    if (allTasks) {
      const idx = allTasks.findIndex(t => t.task_key === taskKey);
      if (idx >= 0) Object.assign(allTasks[idx], task);
    }

    showFieldFeedback(fieldEl, true);
  } catch (err) {
    showToast(err.message || 'Failed to save', 'error');
    showFieldFeedback(fieldEl, false);
  }
}

function showFieldFeedback(el, success) {
  if (!el) return;
  const wrapper = el.closest('.task-detail-field') || el;
  if (success) {
    const check = document.createElement('span');
    check.className = 'task-detail-saved';
    check.textContent = ' \u2713';
    wrapper.appendChild(check);
    setTimeout(() => check.remove(), 800);
  } else {
    wrapper.classList.add('task-detail-field-error');
    setTimeout(() => wrapper.classList.remove('task-detail-field-error'), 400);
  }
}

function startTitleEdit() {
  if (!currentState) return;
  const titleEl = currentState.root.querySelector('.task-detail-title');
  if (!titleEl) return;

  titleEl.contentEditable = 'true';
  titleEl.textContent = currentState.task.title;
  titleEl.classList.add('editing');
  titleEl.focus();

  const range = document.createRange();
  range.selectNodeContents(titleEl);
  window.getSelection().removeAllRanges();
  window.getSelection().addRange(range);

  function save() {
    titleEl.contentEditable = 'false';
    titleEl.classList.remove('editing');
    const newTitle = titleEl.textContent.trim();
    if (newTitle && newTitle !== currentState.task.title) {
      patchField('title', newTitle);
    } else {
      titleEl.textContent = currentState.task.title;
    }
  }

  titleEl.addEventListener('blur', save, { once: true });
  titleEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); titleEl.blur(); }
    if (e.key === 'Escape') { titleEl.textContent = currentState.task.title; titleEl.blur(); }
  });
}

// caretOffsetFromClick(evt, rawText, sourceEl) — hit-tests a click inside the rendered
// markdown (`sourceEl`, still in the DOM) and maps it to an approximate offset in
// `rawText` via caret-map.js's rawOffsetFromRendered(). Must run BEFORE sourceEl is
// replaced — caretRangeFromPoint/caretPositionFromPoint only work against live layout.
// Returns null on any miss (no browser API, click landed on a non-text node such as an
// <img> or padding, hit node outside sourceEl, or no probe matched) — callers treat null
// as "fall back to end-of-text", never throw.
function caretOffsetFromClick(evt, rawText, sourceEl) {
  if (!evt || !sourceEl || typeof evt.clientX !== 'number') return null;
  try {
    let node = null;
    let offset = 0;
    if (typeof document.caretRangeFromPoint === 'function') {
      const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
      if (!range) return null;
      node = range.startContainer;
      offset = range.startOffset;
    } else if (typeof document.caretPositionFromPoint === 'function') {
      const pos = document.caretPositionFromPoint(evt.clientX, evt.clientY);
      if (!pos) return null;
      node = pos.offsetNode;
      offset = pos.offset;
    } else {
      return null; // neither API available (unsupported browser)
    }

    if (!node || node.nodeType !== Node.TEXT_NODE || !sourceEl.contains(node)) return null;

    // Walk sourceEl's text nodes to build the flattened rendered text + the absolute
    // index of the click within it (renderMarkdown() output has no other text source).
    const walker = document.createTreeWalker(sourceEl, NodeFilter.SHOW_TEXT);
    let renderedText = '';
    let renderedIndex = null;
    let n;
    while ((n = walker.nextNode())) {
      if (n === node) renderedIndex = renderedText.length + offset;
      renderedText += n.nodeValue;
    }
    if (renderedIndex == null) return null;

    return rawOffsetFromRendered(renderedText, renderedIndex, rawText);
  } catch {
    return null; // never let a caret hit-test miss break edit-mode entry
  }
}

// placeCaretFromClick(textarea, offset) — focuses `textarea` and sets its caret to
// `offset`, falling back to end-of-text when offset is null (no click evt, or
// caretOffsetFromClick() couldn't resolve one).
function placeCaretFromClick(textarea, offset) {
  textarea.focus();
  const pos = offset == null ? textarea.value.length : offset;
  textarea.setSelectionRange(pos, pos);
}

function startDescriptionEdit(evt) {
  if (!currentState) return;
  const descEl = currentState.root.querySelector('.task-detail-description');
  if (!descEl) return;

  // Measure + hit-test BEFORE descEl is replaced (C1469) — both need the live element.
  const rect = descEl.getBoundingClientRect();
  const rawText = currentState.task.description || '';
  const caretOffset = evt ? caretOffsetFromClick(evt, rawText, descEl) : null;

  const textarea = document.createElement('textarea');
  textarea.className = 'form-textarea task-detail-desc-edit';
  textarea.value = rawText;
  textarea.rows = 8;
  // Copy the rendered block's height so swapping to the textarea causes no panel jump.
  // 120 = the CSS min-height floor (styles.css .task-detail-desc-edit) — never go below
  // it even for a one-line description, so the editor stays comfortably usable.
  textarea.style.height = Math.max(Math.ceil(rect.height), 120) + 'px';
  descEl.replaceWith(textarea);
  // Grow-to-fit: rendered markdown can be SHORTER than its raw source (e.g. a long link
  // renders as short link text), so the copied height may still clip content on entry.
  // Only grow, never shrink — shrinking here would reintroduce the jump this is fixing.
  if (textarea.scrollHeight > textarea.clientHeight) textarea.style.height = textarea.scrollHeight + 'px';
  placeCaretFromClick(textarea, caretOffset);
  // allowFiles:true (C1447) — this field also gets the Embed ▾ picker below, so
  // dropping/pasting a non-image file must work too, not just images.
  attachImageHandlers(textarea, currentState.projectId, currentState.taskKey, { allowFiles: true });

  // @mention typeahead (TPT33). Attached BEFORE the field's own Escape keydown
  // listener below (load-bearing — see mention-typeahead.js's onKeydown comment):
  // when its dropdown is open, Escape calls stopImmediatePropagation(), which only
  // suppresses a SAME-element listener registered after this one. `mentionPicking`
  // mirrors `pickerOpen` just below for the same reason: this field's blur is
  // destructive ({once:true} -> save() -> replaceWith), and a mousedown-commit inside
  // the mention dropdown must not let that blur fire mid-pick. Scoped to the
  // synchronous commit only (unlike `pickerOpen`'s async file-dialog window) — guarding
  // "dropdown open" instead would eat a genuine click-away's one blur and never save.
  let mentionPicking = false;
  const mentionTypeahead = attachMentionTypeahead(textarea, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
    onPickStart: () => { mentionPicking = true; },
    onPickEnd: () => { mentionPicking = false; textarea.focus(); },
  });

  // Embed ▾ button + live thumbnail strip (C1447). `pickerOpen` guards against the
  // {once:true} blur below firing save() (which destroys `textarea` via replaceWith)
  // while the native file dialog is open — some engines fire element blur when the OS
  // dialog takes the window focus, and losing the textarea mid-upload would silently
  // drop the ref insertAtCursor was about to write.
  let pickerOpen = false;
  const embedControls = attachEmbedControls(textarea, {
    projectId: currentState.projectId,
    taskKey: currentState.taskKey,
    anchor: textarea,
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
    onPickerOpen: () => { pickerOpen = true; },
    onPickerDone: () => { pickerOpen = false; textarea.focus(); },
  });

  function save() {
    mentionTypeahead.destroy(); // before replaceWith — same discipline as embedControls below
    embedControls.destroy(); // before replaceWith — its block/listeners must die with the editor

    const newDesc = textarea.value.trim();
    const div = document.createElement('div');
    div.className = 'task-detail-description md-preview';
    div.title = 'Click to edit';

    if (newDesc !== (currentState.task.description || '')) {
      div.innerHTML = descriptionHtml(newDesc, currentState.members);
      textarea.replaceWith(div);
      patchField('description', newDesc);
    } else {
      div.innerHTML = descriptionHtml(currentState.task.description, currentState.members);
      textarea.replaceWith(div);
    }
  }

  function onBlur() {
    if (pickerOpen || mentionPicking) { textarea.addEventListener('blur', onBlur, { once: true }); return; }
    save();
  }
  textarea.addEventListener('blur', onBlur, { once: true });
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      textarea.value = currentState.task.description || '';
      textarea.blur();
    }
  });
}

// mountTagTypeahead() — (re)wires the Tags field's chip-input/typeahead after any paint
// that rewrites currentState.root.innerHTML. Sibling of mountAssigneeCombo() just above,
// same reason: renderPanelContent() runs on every parent change / comment post (via
// refreshPanelContent), so a tag change never triggers that full re-render itself (see
// onChange below) — only every OTHER repaint needs this re-mount. allowCreate:true keeps
// the register-a-new-tag-with-a-required-description flow this field has always had
// (C1038), now living in the shared lib/tag-typeahead.js instead of a bespoke block here.
function mountTagTypeahead() {
  if (!currentState || !currentState.root) return;
  const inputEl = currentState.root.querySelector('.task-detail-tag-input');
  if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
  if (!inputEl) return;

  tagTypeahead = attachTagTypeahead(inputEl, {
    projectId: currentState.projectId,
    initialTags: currentState.task.tags || [],
    allowCreate: true,
    isAlive: () => document.body.contains(inputEl),
    // Panel mode scrolls inside `.task-detail-body`; page mode scrolls the document —
    // same split mountAssigneeCombo() makes above.
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
    onChange: (names) => {
      if (!currentState) return;
      currentState.task.tags = names;
      patchField('tags', names, inputEl);
    },
  });
}

// mountMentionTypeaheads() (TPT33) — (re)wires the @mention typeahead on the comment
// compose box. Sibling of mountAssigneeCombo()/mountTagTypeahead() just above, same
// destroy-and-recreate discipline: the compose textarea is torn down by every
// refreshPanelContent() repaint (comment post, tag/parent change, etc.), so the
// previous instance's DOM is already gone by the time this runs.
function mountMentionTypeaheads() {
  if (!currentState || !currentState.root) return;
  if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
  const ta = currentState.root.querySelector('.task-detail-comment-input');
  if (!ta) return;

  commentMentionTypeahead = attachMentionTypeahead(ta, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
  });
}

// startCommentEdit(row) / cancelCommentEdit() / saveCommentEdit(id) (TPT33) — in-place
// DOM swap for editing an own comment, modeled on startDescriptionEdit() above. NEVER
// routed through refreshPanelContent(): a full repaint would (a) force "which comment
// is editing" into yet another module global purely to survive its own render, (b)
// wipe a half-typed NEW comment out of the compose box (the post handler gets away
// with clearing that box first — an unrelated edit must not), and (c) tear down the
// assignee combo / tag typeahead / any other live mention dropdown mid-interaction —
// exactly what mountAssigneeCombo()'s onCommit already documents avoiding.
function startCommentEdit(row) {
  if (!currentState || !row) return;
  const id = Number(row.dataset.commentId);
  if (!Number.isFinite(id)) return;
  if (editingCommentId != null) cancelCommentEdit(); // only one inline editor at a time

  const comment = comments.find(c => Number(c.id) === id);
  if (!comment) return;
  const bodyEl = row.querySelector('.task-detail-comment-body');
  const editBtn = row.querySelector('.task-detail-comment-edit-btn');
  if (!bodyEl) return;

  const rect = bodyEl.getBoundingClientRect();
  const wrap = document.createElement('div');
  wrap.className = 'task-detail-comment-edit';
  wrap.innerHTML = `
    <textarea class="form-textarea task-detail-comment-edit-input" rows="3"></textarea>
    <div class="task-detail-comment-edit-actions">
      <button type="button" class="btn btn-ghost btn-sm task-detail-comment-cancel-btn">Cancel</button>
      <button type="button" class="btn btn-primary btn-sm task-detail-comment-save-btn">Save</button>
    </div>`;
  const textarea = wrap.querySelector('.task-detail-comment-edit-input');
  textarea.value = comment.content || '';
  textarea.style.minHeight = Math.max(Math.ceil(rect.height), 72) + 'px';

  editingCommentId = id;
  if (editBtn) editBtn.style.display = 'none';
  bodyEl.replaceWith(wrap);
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);

  attachImageHandlers(textarea, currentState.projectId, currentState.taskKey);
  // Attached BEFORE the local Escape keydown listener below — load-bearing, same
  // ordering constraint startDescriptionEdit() documents (mention-typeahead.js's
  // onKeydown calls stopImmediatePropagation() while its dropdown is open, which only
  // suppresses a same-element listener registered after this one).
  editCommentMentionTypeahead = attachMentionTypeahead(textarea, {
    getMembers: () => (currentState ? currentState.members : []),
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
  });

  // No blur-to-save here (unlike the description field) — the task asks for explicit
  // Save/Cancel; clicking away leaves the editor open rather than silently committing.
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); cancelCommentEdit(); }
    else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); saveCommentEdit(id); }
  });
}

function cancelCommentEdit() {
  if (!currentState || editingCommentId == null) return;
  const id = editingCommentId;
  const comment = comments.find(c => Number(c.id) === id);
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  if (comment) replaceCommentRow(id, comment);
}

async function saveCommentEdit(id) {
  if (!currentState || editingCommentId !== id) return;
  const row = currentState.root.querySelector(`.task-detail-comment[data-comment-id="${id}"]`);
  const textarea = row && row.querySelector('.task-detail-comment-edit-input');
  const saveBtn = row && row.querySelector('.task-detail-comment-save-btn');
  if (!textarea) return;
  const content = textarea.value.trim();
  if (!content) { showToast('Comment cannot be empty', 'error'); return; }

  const original = comments.find(c => Number(c.id) === id);
  if (original && content === (original.content || '').trim()) { cancelCommentEdit(); return; }

  const state = currentState; // snapshot — the panel/page can close mid-flight
  if (saveBtn) saveBtn.disabled = true;
  try {
    const { comment } = await api.patch(
      '/projects/' + state.projectId + '/tasks/' + state.taskKey + '/comments/' + id,
      { content }
    );
    if (currentState !== state) return; // stale result — a different task/panel owns state now
    const idx = comments.findIndex(c => Number(c.id) === id);
    if (idx >= 0) comments[idx] = comment; // updated_at now set -> "(edited)" renders
    editingCommentId = null;
    if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
    replaceCommentRow(id, comment);
  } catch (err) {
    if (saveBtn) saveBtn.disabled = false;
    showToast(err.message || 'Failed to save comment', 'error');
    // Leave the textarea + the user's text up — a 403/network blip must not eat their edit.
  }
}

// replaceCommentRow(id, comment) — pure DOM swap, no network: reuses renderCommentRow()
// so the post-edit/post-cancel row is byte-identical to what a full repaint would have
// produced.
function replaceCommentRow(id, comment) {
  if (!currentState || !currentState.root) return;
  const row = currentState.root.querySelector(`.task-detail-comment[data-comment-id="${id}"]`);
  if (!row) return;
  const tmp = document.createElement('div');
  tmp.innerHTML = renderCommentRow(comment, currentState.members).trim();
  row.replaceWith(tmp.firstElementChild);
}

function showParentPicker() {
  if (!currentState) return;
  const allTasks = currentState.options.allTasks || [];
  const task = currentState.task;

  // Exclude self + own children
  const childIds = new Set(allTasks.filter(t => t.parent_id === task.id).map(t => t.id));
  const eligible = allTasks.filter(t => t.id !== task.id && !childIds.has(t.id));

  let html = `
    <input type="text" class="form-input mb-1" id="parent-picker-search" placeholder="Search tasks..." autofocus>
    <div id="parent-picker-list" style="max-height:300px;overflow-y:auto">
      ${eligible.map(t => `
      <div class="task-detail-child parent-picker-item" data-task-id="${t.id}" data-task-key="${escapeHtml(t.task_key)}">
        ${getTypeIcon(t.type || 'task')}
        <span class="badge badge-sm">${escapeHtml(t.task_key)}</span>
        <span>${escapeHtml(t.title)}</span>
      </div>`).join('')}
    </div>`;

  openModal('Select Parent', html, {
    onMount(overlay) {
      const search = overlay.querySelector('#parent-picker-search');
      const list = overlay.querySelector('#parent-picker-list');

      search.addEventListener('input', () => {
        const q = search.value.toLowerCase();
        list.querySelectorAll('.parent-picker-item').forEach(el => {
          const text = el.textContent.toLowerCase();
          el.style.display = text.includes(q) ? '' : 'none';
        });
      });

      list.addEventListener('click', async (e) => {
        const item = e.target.closest('.parent-picker-item');
        if (!item) return;
        const parentId = parseInt(item.dataset.taskId, 10);
        closeModal();
        await patchField('parent_id', parentId);
        refreshPanelContent();
      });
    }
  });
}

function openAddChildModal() {
  if (!currentState) return;
  const { projectId, task } = currentState;

  // Modal-scoped reservation state, closed over by onMount/onClose below.
  let reservedKey = null;
  let committed = false;
  let childAssignee = null;
  let childCombo = null;

  const html = `
    <div class="form-group mb-1">
      <label class="form-label">Task Key</label>
      <input type="text" class="form-input" id="child-task-key" readonly value="Reserving…">
    </div>
    <div class="form-group mb-1">
      <label class="form-label">Title</label>
      <input type="text" class="form-input" id="child-title" placeholder="Task title" required>
    </div>
    <div class="form-group mb-1">
      <label class="form-label">Assignee</label>
      <div class="child-assignee-combo">${renderMemberCombo({ id: 'child-assignee', assignee: null, members: currentState.members })}</div>
    </div>
    <div class="form-group mb-1">
      <label class="form-label">Description</label>
      <textarea class="form-textarea" id="child-description" rows="3" placeholder="Description"></textarea>
    </div>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="child-cancel">Cancel</button>
      <button class="btn btn-primary" id="child-create" disabled>Create</button>
    </div>`;

  openModal('Add Child Task', html, {
    onMount(overlay) {
      const comboRoot = overlay.querySelector('.child-assignee-combo');
      childCombo = createMemberCombo(comboRoot, {
        getMembers: () => (currentState ? currentState.members : []),
        getValue: () => childAssignee,
        setValue: (id) => { childAssignee = id; },
        onCommit: () => {},
        isAlive: () => document.body.contains(comboRoot),
        // `overlay` (passed by ui.js's openModal) is the fixed full-viewport
        // `.modal-overlay`; the actual scroller is its `.modal` child (max-height:
        // 85vh; overflow-y: auto).
        scrollEl: overlay.querySelector('.modal') || overlay,
      });

      // Book the task_key server-side the moment the modal opens, with parent_id
      // set from the start so the child is correctly nested immediately.
      (async () => {
        const keyInput = overlay.querySelector('#child-task-key');
        const createBtn = overlay.querySelector('#child-create');
        try {
          const placeholder = await reserveTaskKey(projectId, {
            category: 'CODING', priority: task.priority, parent_id: task.id,
          });
          reservedKey = placeholder.task_key;
          if (currentState.options.allTasks) currentState.options.allTasks.push(placeholder);
          if (keyInput) keyInput.value = reservedKey;
          if (createBtn) createBtn.disabled = false;
        } catch (err) {
          showToast(err.message || 'Failed to reserve task key', 'error');
          closeModal();
        }
      })();

      overlay.querySelector('#child-cancel').addEventListener('click', closeModal);
      overlay.querySelector('#child-create').addEventListener('click', async () => {
        if (!reservedKey) return; // reservation still in flight; button is disabled anyway

        const title = overlay.querySelector('#child-title').value.trim();
        const description = overlay.querySelector('#child-description').value.trim();

        if (!title) {
          showToast('Title required', 'error');
          return;
        }

        const btn = overlay.querySelector('#child-create');
        btn.disabled = true;
        btn.textContent = 'Creating...';

        try {
          // Finalize the already-reserved task_key in place — never a second create.
          const { task: newTask } = await api.patch('/projects/' + projectId + '/tasks/' + reservedKey, {
            title,
            description,
            assignee: childAssignee,
          });

          if (currentState.options.allTasks) {
            const idx = currentState.options.allTasks.findIndex(t => t.task_key === reservedKey);
            if (idx >= 0) currentState.options.allTasks[idx] = newTask;
          }

          committed = true;
          closeModal();
          showToast('Child task created', 'success');
          refreshPanelContent();
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Create';
          showToast(err.message || 'Failed to create task', 'error');
        }
      });
    },
    onClose() {
      if (childCombo) { childCombo.destroy(); childCombo = null; }
      // Modal dismissed (Cancel, ×, Esc, backdrop) before submit: release the
      // booked key so it's free for the next task.
      if (committed || !reservedKey) return;
      const key = reservedKey;
      reservedKey = null;
      releaseTaskKey(projectId, key);
      // Mutate in place — board.js/etc. hold a reference to this exact array and
      // reassigning currentState.options.allTasks here wouldn't be visible to them.
      const allTasks = currentState && currentState.options.allTasks;
      if (allTasks) {
        const idx = allTasks.findIndex(t => t.task_key === key);
        if (idx >= 0) allTasks.splice(idx, 1);
      }
    },
  });
}

function confirmDelete() {
  if (!currentState) return;
  const { projectId, taskKey } = currentState;

  openModal('Delete Task', `
    <p>Delete task <strong>${escapeHtml(taskKey)}</strong>?</p>
    <p class="text-muted mt-1">This cannot be undone.</p>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
      <button class="btn btn-danger" id="confirm-delete-btn">Delete</button>
    </div>
  `, {
    onMount(overlay) {
      overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
      overlay.querySelector('#confirm-delete-btn').addEventListener('click', async () => {
        const btn = overlay.querySelector('#confirm-delete-btn');
        btn.disabled = true;
        btn.textContent = 'Deleting...';
        try {
          await api.delete('/projects/' + projectId + '/tasks/' + taskKey);

          // Remove from allTasks
          if (currentState.options.allTasks) {
            const idx = currentState.options.allTasks.findIndex(t => t.task_key === taskKey);
            if (idx >= 0) currentState.options.allTasks.splice(idx, 1);
          }

          closeModal();
          showToast('Task deleted', 'success');
          if (currentState && currentState.mode === 'page') {
            const pid = currentState.projectId;
            currentState = null;
            navigate('/projects/' + pid + '/board');
          } else {
            closeTaskDetail();
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Delete';
          showToast(err.message || 'Failed to delete', 'error');
        }
      });
    }
  });
}

// mountAssigneeCombo() — (re)wires the Member Assignee combo-box after any paint that
// rewrites currentState.root.innerHTML. renderPanelContent() runs on every tag add/
// remove, parent change, and comment post (via refreshPanelContent), so the combo
// instance from the previous paint is dead DOM by the time this fires — destroy it and
// mount a fresh one against the freshly rendered `.task-detail-assignee-combo` root.
function mountAssigneeCombo() {
  if (!currentState || !currentState.root) return;
  const comboRoot = currentState.root.querySelector('.task-detail-assignee-combo');
  if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
  if (!comboRoot) return;

  assigneeCombo = createMemberCombo(comboRoot, {
    getMembers: () => (currentState ? currentState.members : []),
    getValue: () => (currentState ? currentState.task.assignee : null),
    setValue: (id) => { if (currentState) currentState.task.assignee = id; },
    onCommit: (userId) => {
      if (!currentState) return;
      // Update the sibling avatar in place — deliberately not refreshPanelContent(),
      // which would tear down this very combo mid-interaction and steal focus.
      const avatarEl = currentState.root.querySelector('.task-detail-assignee-avatar');
      if (avatarEl) {
        const member = userId ? (currentState.members.find(m => m.user_id === userId) || null) : null;
        avatarEl.innerHTML = renderAvatarHtml(member, 24);
      }
      const inputEl = comboRoot.querySelector('.member-combo-input');
      patchField('assignee', userId, inputEl);
    },
    isAlive: () => document.body.contains(comboRoot),
    // Panel mode scrolls inside `.task-detail-body` (overflow-y: auto); page mode
    // deliberately neutralizes that container's overflow and lets the document
    // scroll instead (see `.task-detail-page .task-detail-body` in styles.css) — the
    // reposition listener has to target whichever element is actually scrolling.
    scrollEl: currentState.mode === 'page'
      ? window
      : (currentState.root.querySelector('.task-detail-body') || currentState.root),
  });
}

function refreshPanelContent() {
  if (!currentState || !currentState.root) return;
  // The rows the Notifications tab's IntersectionObserver is watching are about to be
  // discarded by the innerHTML rewrite below — disconnect first, re-arm after if that
  // tab is still the active one (TPT18).
  disconnectEventObserver();
  // A live inline comment editor (TPT33) is about to be discarded by the innerHTML
  // rewrite below too — drop its state so nothing (the editing flag, its mention
  // dropdown) survives pointing at DOM that's about to be gone.
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  const children = (currentState.options.allTasks || []).filter(t => t.parent_id === currentState.task.id);
  currentState.root.innerHTML = renderPanelContent(currentState.task, currentState.members, children);
  const commentInput = currentState.root.querySelector('.task-detail-comment-input');
  if (commentInput) attachImageHandlers(commentInput, currentState.projectId, currentState.taskKey);
  mountAssigneeCombo();
  mountTagTypeahead();
  mountMentionTypeaheads();
  if (activeTab === 'notifications') observeUnreadEvents();
}

// switchTab(tab) — (TPT18) toggles the Details/Comments/Notifications tab strip's
// active button + which `.task-detail-tab-panel` is visible, without a full repaint
// (renderPanelContent() only runs on data changes, not on a tab click). `activeTab` is
// a module global (not part of currentState) for the same reason `comments` is:
// refreshPanelContent() wipes currentState.root.innerHTML wholesale, and the freshly
// rendered markup reads `activeTab` to restore whichever tab was showing.
function switchTab(tab) {
  if (!currentState || !currentState.root) return;
  activeTab = tab;
  currentState.root.querySelectorAll('.task-detail-tab').forEach(btn => {
    const isActive = btn.dataset.tab === tab;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-selected', String(isActive));
  });
  currentState.root.querySelectorAll('.task-detail-tab-panel').forEach(panel => {
    panel.hidden = panel.dataset.tab !== tab;
  });
  if (tab === 'notifications') observeUnreadEvents();
  else disconnectEventObserver();
}

// observeUnreadEvents()/disconnectEventObserver() — (TPT18) per-row "seen" tracking for
// the Notifications tab, adapted from the Task App modal's comment-seen IntersectionObserver
// (task-board.js `_observeVisibleComments`). Only rows still unread-and-unobserved are
// watched; once a row scrolls into view it's marked observed and the tab badge is
// recomputed in place (no full repaint, so the observer isn't torn down mid-scroll).
// The last-seen marker is persisted only once EVERY currently-unread row has been seen
// this session — a single high-water id, so a partial scroll never marks later, still-
// unseen rows as read.
function observeUnreadEvents() {
  disconnectEventObserver();
  if (!currentState || !currentState.root) return;
  const listEl = currentState.root.querySelector('.task-detail-events-list');
  if (!listEl) return;

  const currentUser = getUser();
  const seenEventId = getSeenEventId(currentState.projectId, currentState.taskKey);
  const unreadIds = new Set(
    unreadEventIds(taskEvents, seenEventId, currentUser && currentUser.id)
      .filter(id => !observedEventIds.has(id))
  );
  if (unreadIds.size === 0) return;

  // Panel mode scrolls inside `.task-detail-body`; page mode neutralizes that
  // container's overflow and lets the document scroll instead — same mode-dependent
  // root mountAssigneeCombo()'s scrollEl already uses.
  const root = currentState.mode === 'page'
    ? null
    : (currentState.root.querySelector('.task-detail-body') || null);

  eventObserver = new IntersectionObserver((entries) => {
    let changed = false;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const id = Number(entry.target.dataset.eventId);
      if (!unreadIds.has(id)) continue;
      unreadIds.delete(id);
      observedEventIds.add(id);
      eventObserver.unobserve(entry.target);
      changed = true;
    }
    if (!changed || !currentState || !currentState.root) return;
    const badgeHost = currentState.root.querySelector('.task-detail-tab[data-tab="notifications"]');
    if (badgeHost) {
      const remaining = unreadIds.size;
      let badge = badgeHost.querySelector('.task-detail-tab-badge');
      if (remaining === 0) {
        if (badge) badge.remove();
        setSeenEventId(currentState.projectId, currentState.taskKey, maxEventId(taskEvents));
      } else {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'task-detail-tab-badge';
          badgeHost.appendChild(badge);
        }
        badge.textContent = remaining > 99 ? '99+' : String(remaining);
      }
    }
  }, { root, threshold: 0.1 });

  listEl.querySelectorAll('.task-detail-event[data-event-id]').forEach(row => {
    if (unreadIds.has(Number(row.dataset.eventId))) eventObserver.observe(row);
  });
}

function disconnectEventObserver() {
  if (eventObserver) { eventObserver.disconnect(); eventObserver = null; }
}

// bindDetailEvents(el) — binds the 3 delegated listeners to `el` (panelEl for the
// slide-out, a page host for the standalone route) and returns an unbind fn. Every
// handler opens with an ownership check: currentState.root !== e.currentTarget means
// this surface doesn't currently own currentState, so it no-ops. Without this guard,
// binding the same handlers to two live elements would let a click on one surface
// mutate state owned by the other.
function bindDetailEvents(el) {
  if (!el) return () => {};

  const onClick = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Tab strip (TPT18) — Details / Comments / Notifications
    const tabBtn = e.target.closest('.task-detail-tab');
    if (tabBtn) {
      switchTab(tabBtn.dataset.tab);
      return;
    }

    // Notifications tab: manual refresh
    if (e.target.closest('.task-detail-events-refresh')) {
      const btn = e.target.closest('.task-detail-events-refresh');
      const { projectId, taskKey } = currentState;
      btn.disabled = true;
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100')
        .then((res) => {
          taskEvents = res.events || [];
          eventsSubscribed = res.subscribed !== false;
          refreshPanelContent();
        })
        .catch(err => showToast(err.message || 'Failed to load notifications', 'error'))
        .finally(() => { btn.disabled = false; });
      return;
    }

    // Close button (panel mode only — page mode never renders it)
    if (e.target.closest('.task-detail-close')) {
      closeTaskDetail();
      return;
    }

    // Title click → edit
    if (e.target.closest('.task-detail-title') && !e.target.closest('[contenteditable="true"]')) {
      startTitleEdit();
      return;
    }

    // Description click → edit
    if (e.target.closest('.task-detail-description')) {
      startDescriptionEdit(e);
      return;
    }

    // Remove parent
    if (e.target.closest('.task-detail-remove-parent')) {
      patchField('parent_id', null).then(() => refreshPanelContent());
      return;
    }

    // Change parent
    if (e.target.closest('.task-detail-change-parent')) {
      showParentPicker();
      return;
    }

    // Parent link click → navigate to parent (page mode: real <a href> handles it natively)
    const parentLink = e.target.closest('.detail-parent-link');
    if (parentLink) {
      if (currentState.mode === 'page') return;
      const parentKey = parentLink.dataset.taskKey;
      if (parentKey) {
        closeTaskDetail();
        setTimeout(() => {
          openTaskDetail(currentState.projectId, parentKey, currentState.options);
        }, 220);
      }
      return;
    }

    // Child click → navigate to child (page mode: real <a href> handles it natively)
    const childEl = e.target.closest('.task-detail-child:not(.parent-picker-item)');
    if (childEl) {
      if (currentState.mode === 'page') return;
      const childKey = childEl.dataset.taskKey;
      if (childKey) {
        closeTaskDetail();
        setTimeout(() => {
          openTaskDetail(currentState.projectId, childKey, currentState.options);
        }, 220);
      }
      return;
    }

    // Add child
    if (e.target.closest('.task-detail-add-child')) {
      openAddChildModal();
      return;
    }

    // Delete
    if (e.target.closest('.task-detail-delete-btn')) {
      confirmDelete();
      return;
    }

    // Comment submit
    if (e.target.closest('.task-detail-comment-submit')) {
      const btn = e.target.closest('.task-detail-comment-submit');
      const ta = currentState.root.querySelector('.task-detail-comment-input');
      const content = ta?.value.trim();
      if (!content) return;
      btn.disabled = true;
      api.post('/projects/' + currentState.projectId + '/tasks/' + currentState.taskKey + '/comments', { content })
        .then(({ comment }) => { comments.push(comment); ta.value = ''; refreshPanelContent(); })
        .catch(err => showToast(err.message || 'Failed to post comment', 'error'))
        .finally(() => { btn.disabled = false; });
      return;
    }

    // Comment edit (TPT33) — Edit / Save / Cancel on an own comment's inline editor.
    // Distinct class names (not `-edit`/`-edit-save`) so closest() token matching is
    // never ambiguous between the three.
    if (e.target.closest('.task-detail-comment-edit-btn')) {
      startCommentEdit(e.target.closest('.task-detail-comment'));
      return;
    }
    if (e.target.closest('.task-detail-comment-save-btn')) {
      saveCommentEdit(editingCommentId);
      return;
    }
    if (e.target.closest('.task-detail-comment-cancel-btn')) {
      cancelCommentEdit();
      return;
    }
  };

  const onChange = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Type select
    if (e.target.closest('.task-detail-type-select')) {
      patchField('type', e.target.value);
      return;
    }

    // Status select
    if (e.target.closest('.task-detail-status-select')) {
      const newStatus = e.target.value;
      const inProgressName = roleStatus(currentState.statuses, 'in_progress')?.name;
      if (newStatus === inProgressName && !canStartTask(currentState.task, getUser())) {
        showToast('Cannot start a task assigned to another member', 'error');
        e.target.value = currentState.task.status; // reset select
        return;
      }
      patchField('status', newStatus);
      return;
    }

    // Priority select — only rendered when project.sprints_enabled (C1334); no guard needed
    // here, an absent select means this branch is structurally unreachable.
    if (e.target.closest('.task-detail-priority-select')) {
      patchField('priority', parseInt(e.target.value, 10));
      return;
    }

    // Notifications tab (TPT18): Subscribed mute/follow toggle — optimistic, reverted
    // on failure. Sends a strict boolean; the API 400s on anything else.
    if (e.target.closest('.task-detail-subscribe-toggle')) {
      const checkbox = e.target;
      const next = checkbox.checked;
      const { projectId, taskKey } = currentState;
      api.put('/projects/' + projectId + '/tasks/' + taskKey + '/subscription', { subscribed: next })
        .then((res) => { eventsSubscribed = res.subscribed !== false; })
        .catch(err => {
          checkbox.checked = !next;
          showToast(err.message || 'Failed to update subscription', 'error');
        });
      return;
    }
  };

  const onBlur = (e) => {
    if (!currentState || currentState.root !== e.currentTarget) return;

    // Story points
    if (e.target.closest('.task-detail-sp-input')) {
      const val = e.target.value.trim();
      const sp = val === '' ? null : parseInt(val, 10);
      if (sp !== currentState.task.story_points) {
        patchField('story_points', sp);
      }
    }
  };

  el.addEventListener('click', onClick);
  el.addEventListener('change', onChange);
  el.addEventListener('blur', onBlur, true); // capture — inputs don't bubble blur

  return () => {
    el.removeEventListener('click', onClick);
    el.removeEventListener('change', onChange);
    el.removeEventListener('blur', onBlur, true);
  };
}

export async function openTaskDetail(projectId, taskKey, options = {}) {
  ensurePanel();
  panelSeq++;

  // Show loading state
  panelEl.innerHTML = '<div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>';
  overlayEl.classList.add('open');
  panelEl.classList.add('open');
  document.body.classList.add('task-detail-open');

  // Bind escape
  if (escHandler) document.removeEventListener('keydown', escHandler);
  escHandler = (e) => {
    if (e.key === 'Escape' && !document.querySelector('.modal-overlay')) {
      closeTaskDetail();
    }
  };
  document.addEventListener('keydown', escHandler);

  comments = [];
  taskEvents = [];
  eventsSubscribed = true;
  activeTab = 'details';
  observedEventIds = new Set();
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  try {
    const [taskRes, membersRes, commentsRes, sprintsRes, statuses, project, devices, eventsRes] = await Promise.all([
      api.get('/projects/' + projectId + '/tasks/' + taskKey),
      api.get('/projects/' + projectId + '/members'),
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/comments').catch(() => ({ comments: [] })),
      api.get('/projects/' + projectId + '/sprints').catch(() => ({ sprints: [] })),
      loadStatuses(projectId),
      // C1334/C1395 — sprints_enabled gates the Sprint field below, task_group_label names
      // it. Self-fetched rather than threaded through options: this module is opened from 6
      // panel call sites plus the standalone page, and every one would have to pass it.
      // Via the shared per-project cache (lib/project.js, C1395) rather than a fresh fetch
      // per open — a Settings-tab save calls invalidateProject() itself, so this still never
      // shows a stale label/toggle past the save that changed it. Fails open to `null`,
      // which sprintsEnabled() reads as ON — never hides a live field over a blip.
      loadProject(projectId),
      // C1389 — gates the "Start in Task App" button (is_objective tasks only, see
      // renderPanelContent()). Both entry points fetch this for the same reason
      // loadStatuses()/loadProject() are fetched by both — see § Two Surfaces, One
      // Renderer. Fails open to [] (lib/devices.js), so a devices hiccup only disables
      // the button, never blocks the panel.
      loadDevices(),
      // (TPT10 backend, TPT18 first web caller) — Notifications tab event log + this
      // user's effective subscribed state. Fails open (empty log, subscribed default-on)
      // so an events hiccup never blocks the rest of the panel, same discipline as the
      // comments/sprints legs above.
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100').catch(() => ({ events: [], total: 0, subscribed: true })),
    ]);

    const task = taskRes.task;
    const members = membersRes.members || [];
    const children = (options.allTasks || []).filter(t => t.parent_id === task.id);
    comments = commentsRes.comments || [];
    taskEvents = eventsRes.events || [];
    eventsSubscribed = eventsRes.subscribed !== false;

    options.sprints = sprintsRes.sprints || [];
    currentState = { projectId, taskKey, task, members, options, statuses: statuses || [], project, devices: devices || [], root: panelEl, mode: 'panel' };

    panelEl.innerHTML = renderPanelContent(task, members, children);
    const commentInput = panelEl.querySelector('.task-detail-comment-input');
    if (commentInput) attachImageHandlers(commentInput, projectId, taskKey);
    mountAssigneeCombo();
    mountTagTypeahead();
    mountMentionTypeaheads();
  } catch (err) {
    panelEl.innerHTML = `<div class="task-detail-body"><div class="empty text-muted">Failed to load task: ${escapeHtml(err.message)}</div></div>`;
  }
}

export function closeTaskDetail() {
  if (!panelEl || !overlayEl) return;

  removeAssigneePicker();
  if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
  if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
  if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
  editingCommentId = null;
  // Closing doesn't remove panelEl from the DOM (it slides out via CSS class), so a
  // textarea mid-edit never blurs here — its {once:true} blur -> save() (which would
  // destroy the embed block) simply never fires. The next openTaskDetail() overwrites
  // panelEl.innerHTML directly, which would leak any open Embed menu's document-level
  // mousedown/keydown listeners without this explicit close (C1447). Same reasoning for
  // the Notifications tab's IntersectionObserver (TPT18) — it would otherwise keep
  // observing rows that the next openTaskDetail() has already thrown away — and for the
  // mention typeahead (TPT33): a stray open dropdown otherwise outlives the panel.
  closeAnyOpenEmbedMenu();
  closeAnyOpenMentionTypeahead();
  disconnectEventObserver();
  panelEl.classList.remove('open');
  overlayEl.classList.remove('open');
  document.body.classList.remove('task-detail-open');

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  // Only this panel's own ownership of currentState is ours to clear — a
  // standalone task page may currently own it (mode: 'page'), unrelated to
  // this panel's chrome being closed.
  if (!currentState || currentState.root !== panelEl) return;

  const onClose = currentState.options.onClose;
  currentState = null;

  // Call onClose after transition — but only if no other panel opened in the meantime
  // (see panelSeq comment above the declaration): a re-render triggered by this onClose
  // (C1261) would otherwise force-close a drawer the user opened right after this one.
  const seq = panelSeq;
  if (onClose) {
    setTimeout(() => { if (panelSeq === seq) onClose(); }, 220);
  }
}

// mountTaskDetail(hostEl, projectId, taskKey, options) — standalone-page counterpart
// to openTaskDetail(). A sibling, not a refactor of it: openTaskDetail flips on the
// panel's slide-in classes BEFORE the fetch resolves so the animation starts
// immediately, which is panel-chrome-specific and doesn't apply to a page host.
// Rethrows on failure so the caller (taskDetailPage) owns its own error UI —
// it knows the project id for a "back to board" link, this module doesn't.
export async function mountTaskDetail(hostEl, projectId, taskKey, options = {}) {
  // renderPanelContent()/refreshPanelContent() only ever rewrite hostEl.innerHTML, never its
  // className — the page-mode styling hook (.task-detail-page, styles.css) has to be asserted
  // here instead of left to whatever class the caller's host element happened to be given.
  hostEl.classList.add('task-detail-page');
  hostEl.innerHTML = '<div class="task-detail-body"><div class="empty"><span class="spinner"></span></div></div>';
  const unbind = bindDetailEvents(hostEl);
  comments = [];
  taskEvents = [];
  eventsSubscribed = true;
  activeTab = 'details';
  observedEventIds = new Set();
  editingCommentId = null;
  if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }

  try {
    const [taskRes, membersRes, commentsRes, sprintsRes, statuses, project, devices, eventsRes] = await Promise.all([
      api.get('/projects/' + projectId + '/tasks/' + taskKey),
      api.get('/projects/' + projectId + '/members'),
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/comments').catch(() => ({ comments: [] })),
      api.get('/projects/' + projectId + '/sprints').catch(() => ({ sprints: [] })),
      loadStatuses(projectId),
      // C1334/C1395 — same leg as openTaskDetail() above; both entry points share
      // renderPanelContent() and must agree on whether the Sprint field is shown and named.
      loadProject(projectId),
      // C1389 — same leg as openTaskDetail() above; both must agree on button visibility.
      loadDevices(),
      // (TPT10 backend, TPT18 first web caller) — same leg as openTaskDetail() above;
      // both entry points must agree on the Notifications tab's content/badge.
      api.get('/projects/' + projectId + '/tasks/' + taskKey + '/events?limit=100').catch(() => ({ events: [], total: 0, subscribed: true })),
    ]);

    const task = taskRes.task;
    const members = membersRes.members || [];
    comments = commentsRes.comments || [];
    taskEvents = eventsRes.events || [];
    eventsSubscribed = eventsRes.subscribed !== false;
    options.sprints = sprintsRes.sprints || [];

    currentState = { projectId, taskKey, task, members, options, statuses: statuses || [], project, devices: devices || [], root: hostEl, mode: 'page' };

    const children = (options.allTasks || []).filter(t => t.parent_id === task.id);
    hostEl.innerHTML = renderPanelContent(task, members, children);
    const commentInput = hostEl.querySelector('.task-detail-comment-input');
    if (commentInput) attachImageHandlers(commentInput, projectId, taskKey);
    mountAssigneeCombo();
    mountTagTypeahead();
    mountMentionTypeaheads();
  } catch (err) {
    unbind();
    throw err;
  }

  return () => {
    unbind();
    disconnectEventObserver();
    closeAnyOpenMentionTypeahead();
    if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
    if (tagTypeahead) { tagTypeahead.destroy(); tagTypeahead = null; }
    if (commentMentionTypeahead) { commentMentionTypeahead.destroy(); commentMentionTypeahead = null; }
    if (editCommentMentionTypeahead) { editCommentMentionTypeahead.destroy(); editCommentMentionTypeahead = null; }
    editingCommentId = null;
    hostEl.classList.remove('task-detail-page');
    if (currentState && currentState.root === hostEl) currentState = null;
  };
}

// Init panel + bind events on module load (delegation — safe before content exists)
ensurePanel();
bindDetailEvents(panelEl);
